'use strict';

import { isElement, forEach, debounce } from 'lodash';
import mustache from 'mustache';

import containerManager from '../../01-helpers/filter-context/container-manager';
import { get as getPageOptions } from '../../01-helpers/utilities/options';
import { stringToNode } from '../../01-helpers/utilities/node';
import { publish, subscribe } from '../../01-helpers/event-manager';

import { FILTER_CHANGED, FILTERAPI_FAILED } from '../../02-const/event-names';

import { GoogleAnalyticsDataLayer } from '../../05-domain/google-analytics/data-layer';

const moduleName = 'pagination',
    cssClasses = {
        gaDataLayer: 'js-ga-data-layer'
    },
    selectors = {
        base: `.js-${moduleName}`,
        container: `.js-${moduleName}-container`,
        scrollElement: `.js-${moduleName}-scrollTo`
    },
    optionNames = {
        nodeId: 'NodeId'
    },
    template = {
        button: `<li class="js-ga-data-layer"><button type="button" class="c-navigation-paging__button  js-ga-data-layer-cta {{# isActive }}c-navigation-paging__button--is-active{{/ isActive }}" data-ga-identifier-pagination-number data-page="{{ buttonText }}">
            <span class="u-visually-hidden">page </span>{{ buttonText }}
        </button><li>`,
        prevButton: `<li class="js-ga-data-layer"><button type="button" {{ disabled }} class="c-navigation-paging__button c-navigation-paging__button--previous-page js-ga-data-layer-cta" data-ga-identifier-pagination-arrow="previous" data-page="{{ page }}">
            <span class="u-visually-hidden">
                {{ buttonText }}
            </span>
        </button><li>`,
        nextButton: `<li class="js-ga-data-layer"><button type="button" {{ disabled }} class="c-navigation-paging__button c-navigation-paging__button--next-page js-ga-data-layer-cta" data-ga-identifier-pagination-arrow="next" data-page="{{ page }}">
            <span class="u-visually-hidden">
                {{ buttonText }}
            </span>
        </button><li>
        `
    },
    translationsKey = {
        key: 'translations',
        Previous: 'Previous',
        Next: 'Next'
    };

function renderTemplate(template, item) {
    return mustache.render(template, item);
}

/**
 * Pagination Module
 * 
 * * Handle pagination logic
 * * Update pagination after filtering
 * * Hide pagination if needed
 *  
 **/

export class Pagination {

    constructor(baseElement) {
        if (!isElement(baseElement)) {
            return false;
        }

        this._configure(baseElement);
        this._setup();
    }

    _reloadPagination(currentState) {
        // check if there is a response from webservice
        if (currentState.response) {
            const response = currentState.response,
                currentPage = response.CurrentPage,
                totalPages = response.TotalPages;

            this._clearPagination.call(this);

            if (!isElement(this.container)) {
                return false;
            }

            if (totalPages > 1) {
                const prevButton = stringToNode(renderTemplate(template.prevButton, { buttonText: this.translations[translationsKey.Previous], page: currentPage-1, disabled: currentPage === 1 ? 'disabled':'', isActive: '' })),
                    nextButton = stringToNode(renderTemplate(template.nextButton, { buttonText: this.translations[translationsKey.Next], page: currentPage+1, disabled: currentPage === totalPages ? 'disabled' : '', isActive: '' }));

                // add previous button
                this.container.appendChild(prevButton);
                prevButton.addEventListener('click', debounce((event) => {
                        this._onClickHandler(event)
                    }, 250), true
                );

                let i = currentPage === 1 ? currentPage : currentPage - 1,
                    loopUntil = currentPage < totalPages ? currentPage + 1 : totalPages;

                // if totalPages is 5 or less, we show alle pages
                if (totalPages <= 5) {
                    i = 1;
                    loopUntil = totalPages
                }

                // if totalPages is 5 or less, we show alle pages
                if (totalPages > 5 && currentPage > 2) {
                    // add first pagebutton
                    const firstPageButton = stringToNode(renderTemplate(template.button, {buttonText: 1}));
                    this.container.appendChild(firstPageButton);
                    firstPageButton.addEventListener('click', debounce((event) => {
                            this._onClickHandler(event)
                        }, 250), true
                    ); 

                    // don't show ... if currentpage is 3 from the beginning.
                    if(currentPage > 3) 
                        this.container.appendChild(stringToNode(`<li>...</li>`));
                }

                // add numbered pages
                for (i; i <= loopUntil; i++) {
                    const json = {
                        buttonText: i,
                        isActive: i === currentPage
                    }

                    const pageButton = stringToNode(renderTemplate(template.button, json));
                    this.container.appendChild(pageButton);
                    pageButton.addEventListener('click', debounce((event) => {
                            this._onClickHandler(event)
                        }, 250), true
                    );
                }

                // if totalPages is 5 or less, we show alle pages
                if (totalPages > 5 && currentPage < totalPages - 1) {
                    // don't show ... if currentpage is 2 of the end.
                    if (currentPage < totalPages - 2) 
                        this.container.appendChild(stringToNode(`<li>...</li>`));

                    // add last pagebutton
                    const lastPageButton = stringToNode(renderTemplate(template.button, { buttonText: totalPages }));
                    this.container.appendChild(lastPageButton);
                    lastPageButton.addEventListener('click', debounce((event) => {
                            this._onClickHandler(event)
                        }, 250), true
                    );
                }
                // add next button
                this.container.appendChild(nextButton);
                nextButton.addEventListener('click', debounce((event) => {
                        this._onClickHandler(event)
                    }, 250), true
                );

                forEach(this.container.children, (item) => {
                    if (item.classList.contains(cssClasses.gaDataLayer)) {
                        new GoogleAnalyticsDataLayer(item);
                    }
                    
                })
            }
        }
    }

    _clearPagination() {
        if (isElement(this.container)) {
            this.container.innerHTML = '';
        }
    }

    _updateFilterContext() {
        let tFilters = this.filterContext.state.Filters;
        return tFilters;
    }

    _configure(baseElement) {
        this.baseElement = baseElement;
        this.filterContext = containerManager.getContainer();
        this.container = this.baseElement.querySelector(selectors.container);
    }

    _setup() {
        this.NodeId = getPageOptions(optionNames.nodeId);
        this.translations = getPageOptions(translationsKey.key);

        this.bindEvents.call(this);
    }

    _onClickHandler(evt) {
        // get value and update filterContext
        const element = evt.currentTarget || evt.target;

        let tFilters = this._updateFilterContext();

        // Update the filterContext
        this.filterContext.update(
            {
                NodeId: this.NodeId,
                Page: parseInt(element.dataset.page),
                Filters: tFilters
            },
            { refresh: 'true' }
        );

        document.querySelector(selectors.scrollElement)?.scrollIntoView({ behavior: "smooth" });

        publish(FILTER_CHANGED);
    }

    bindEvents() {
        forEach(this.baseElement.querySelectorAll('button'), (button) => {
            button.addEventListener('click', debounce((event) => {
                this._onClickHandler(event)
            }, 250), true);
        });
        // what do we do when a page is clicked
        // what do we do when we receive a filterContext.onUpdate
        this.filterContext.onUpdate(({ currentState }) => this._reloadPagination(currentState));
        subscribe(FILTERAPI_FAILED, this._clearPagination.bind(this));
    }
}

const initPagination = () => {
    forEach(document.querySelectorAll(selectors.base), (paginationElement) => {
        new Pagination(paginationElement);
    });
};

initPagination();
